import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { PlatformSetting, SetTestBusinessRequest, TestBusiness, UnitDisplayMode, UnitOfMeasureDisplay } from '../models/platform-settings.model';

/**
 * Platform-scoped settings, served by SubscriptionManagement.API (api/PlatformSettings).
 * Values that belong to the platform and have exactly one value across every tenant —
 * not business configuration, which lives on the business itself.
 */
@Injectable({ providedIn: 'root' })
export class PlatformSettingsService {
  private readonly baseUrl = environment.subscriptionApiUrl + '/api/PlatformSettings';

  constructor(private http: HttpClient) {}

  getAll(): Observable<PlatformSetting[]> {
    return this.http.get<PlatformSetting[]>(this.baseUrl);
  }

  /**
   * The designated test business. The API answers 204 when none is set — a normal state
   * of a working platform, not an error — which arrives here as null.
   */
  getDefaultTestBusiness(): Observable<TestBusiness | null> {
    return this.http.get<TestBusiness | null>(`${this.baseUrl}/DefaultTestBusiness`);
  }

  setDefaultTestBusiness(businessId: string): Observable<TestBusiness> {
    const body: SetTestBusinessRequest = { businessId };
    return this.http.put<TestBusiness>(`${this.baseUrl}/DefaultTestBusiness`, body);
  }

  clearDefaultTestBusiness(): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/DefaultTestBusiness`);
  }

  /**
   * The words an organiser may call a seat on a table ticket. One list for the whole
   * platform; organisers read it through their own app and never see these settings.
   */
  getSeatWords(): Observable<string[]> {
    return this.http.get<string[]>(`${this.baseUrl}/SeatWords`);
  }

  /** Replaces the list, in the order organisers are offered the words. */
  setSeatWords(words: string[]): Observable<string[]> {
    return this.http.put<string[]>(`${this.baseUrl}/SeatWords`, { words });
  }

  /** How a price's unit of measure reads everywhere a price carries one: "code" (KG) or "name" (Kilogram). */
  getUnitOfMeasureDisplay(): Observable<UnitOfMeasureDisplay> {
    return this.http.get<UnitOfMeasureDisplay>(`${this.baseUrl}/UnitOfMeasureDisplay`);
  }

  setUnitOfMeasureDisplay(mode: UnitDisplayMode): Observable<UnitOfMeasureDisplay> {
    return this.http.put<UnitOfMeasureDisplay>(`${this.baseUrl}/UnitOfMeasureDisplay`, { mode });
  }
}
